import {
    Component,
    OnInit,
    Inject,
    Output,
    EventEmitter,
    QueryList,
    ElementRef,
    ViewChildren,
    ViewChild, OnDestroy
} from '@angular/core';
import {MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {MatTreeFlatDataSource, MatTreeFlattener, MatTreeNestedDataSource} from '@angular/material/tree';
import {NgxSpinnerService} from 'ngx-spinner';
import {NotifierService} from '@services/notifier.service';
import {AppService} from '@app/app.service';
import {FlatTreeControl, NestedTreeControl} from '@angular/cdk/tree';
import {TreeNode} from './interfaces/settings.table.dialog.interface';
import {InterfaceSettingsUserTree} from '../../table.interface';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {SettingsTableNameDialogComponent} from '../settings-table-name/settings.table.name.dialog.component';
import {IAppRequest, TreeNodeRegister} from '@app/app.interface';
import {SettingsTableService} from '@app/modules/table/dialogs/settings-table/settings-table.service';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {NgxSkeletonLoaderConfigTheme} from 'ngx-skeleton-loader';
import {map} from 'rxjs/operators';
import {AppDialogService} from '@components/dialog/services/app-dialog.service';
import {SKELETON_LOADER_TOKEN} from '@constants/diConstant';

const getLevel = (node: TreeNodeRegister) => node.level;

const isExpandable = (node: TreeNodeRegister) => node.expandable;

const getChildren = (node: TreeNode): Observable<TreeNode[]> => of(node.children);

const transformer = (node: TreeNode, level: number): TreeNode => {
    const flatNode = node;
    flatNode.expandable = !!node.children && node.children.length > 0;
    flatNode.level = level;
    return flatNode;
};

@Component({
    selector: 'app-settings-table-dialog',
    providers: [SettingsTableService],
    templateUrl: './settings.table.dialog.component.html',
    styleUrls: ['../settings-table-name/styles/settings.table.dialog.component.scss',
        '../settings-table-name/styles/settings.table.dialog.component.adaptive.scss']
})
export class SettingsTableDialogComponent implements OnInit, OnDestroy {

    /** Контейнер списка "Все" */
    @ViewChild('scrollableContainerAll', { read: ElementRef }) public scrollableContainerAll;

    /** Контейнер списка "Выбрано" */
    @ViewChild('scrollableContainerSelect', { read: ElementRef })public scrollableContainerSelect;

    /** Элементы списка "Все" */
    @ViewChildren('itemRefDataAll') public itemRefDataAll: QueryList<ElementRef>;

    /** Элементы списка "Выбрано" */
    @ViewChildren('itemRefDataSelect') public itemRefDataSelect: QueryList<ElementRef>;

    /** Состояние списков */
    filterState = {
        all: {
            index: -1,
            disableDown: true,
            disableUp: true
        },
        select: {
            index: -1,
            disableDown: true,
            disableUp: true
        }
    };

    private _isLoadTree: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public get isLoadTree(): Observable<boolean> {
        return this._isLoadTree.asObservable();
    }

    /** Активный узел */
    public activeNode: string;

    /** Текст, который нужно подсветить в названии узлов */
    public highlightText: { all: string, select: string } = {
        all: '',
        select: ''
    };

    /** Объект для компановки контейнера и элементов списка */
    public itemsRefData: { all: any, select: any } = {
        all: {
            container: null,
            items: []
        },
        select: {
            container: null,
            items: []
        }
    };

    /**
     * Дерево "Выбрано"
     */
    treeControlSelect = new NestedTreeControl<TreeNode>(node => node.children);
    /**
     * Данные дерева "Выбрано"
     */
    dataSourceSelect = new MatTreeNestedDataSource<TreeNode>();
    /**
     * Дерево "Все"
     */
    treeControlAll = new FlatTreeControl<TreeNode>(getLevel, isExpandable);
    /**
     * Данные дерева "Все"
     */
    dataSourceAll: MatTreeFlatDataSource<TreeNode, TreeNode> = null;
    /**
     * Флаг перетаскивания
     * @var {boolean}
     */
    dragging = false;
    /**
     * Данные дерева "Выбрано"
     */
    public dataSelected = [];
    /**
     * Данные дерева "Все"
     */
    public dataAll = [];
    /**
     * Модель для чекбоксов
     */
    public nodeCheckbox: object = {};

    @Output() isSaveEvent = new EventEmitter<any>();

    constructor(private notifier: NotifierService,
                public appService: AppService,
                private spinner: NgxSpinnerService,
                public dialog: MatDialog,
                private dialogConfirm: AppDialogService,
                public dialogRef: MatDialogRef<SettingsTableDialogComponent>,
                private readonly service: SettingsTableService,
                @Inject(SKELETON_LOADER_TOKEN) public readonly skeletonConf: NgxSkeletonLoaderConfigTheme = null,
                @Inject(MAT_DIALOG_DATA) public data: {
                    tableService: any;
                    dynamicFilter: any;
                    tableModule: string
                    dynamicSystemFields: string[];
                    title: string;
                    settings: InterfaceSettingsUserTree;
                }) {
        // Инициализация объект управления деревом все полей
        this.dataSourceAll = new MatTreeFlatDataSource<TreeNode, TreeNode>(
            this.treeControlAll,
            new MatTreeFlattener(transformer, getLevel,
                isExpandable, getChildren));
    }

    public refreshNodeCheckbox(): void {
        this.dataSelected.forEach(select => {
            if (select?.alias) {
                this.nodeCheckbox[select?.alias] = false;
            }
        });
    }

    public hasChild = (node: TreeNode) => getChildren(node).pipe(map((list) => list && list.length));

    public secondLevel = (_: number, node: TreeNode) => node.level == 1;

    public firstLevel = (_: number, node: TreeNode) => node.level == 0 ;

    /**
     * Определение факта включения менее двух чекбоксов
     * используется для проверки возможности объединения
     */
    hasNodeCheckboxChecked(): boolean {
        let count = 0;
        for (const checkbox in this.nodeCheckbox) {
            if (this.nodeCheckbox.hasOwnProperty(checkbox) && this.nodeCheckbox[checkbox]) {
                count++;
            }
            // если уже два, то дальше не идем
            if (count >= 2) {
                return false;
            }
        }
        return true;
    }

    /** Событие окончания инициализации компонента */
    ngOnInit() {
        Promise.all([this.service.getList(this.data.settings.select),
            this.service.getList(this.data.settings.all)])
            .then((result) => {
                this.dataSelected = result[0];
                this.dataAll = result[1];
                this._isLoadTree.next(true);

                this.refreshTree();

                setTimeout(() => {
                    this.itemsRefData = {
                        all: {
                            container: this.scrollableContainerAll?.nativeElement,
                            items: this.itemRefDataAll
                        },
                        select: {
                            container: this.scrollableContainerSelect?.nativeElement,
                            items: this.itemRefDataSelect
                        }
                    };
                });
            });
    }

    public ngOnDestroy(): void {
        this._isLoadTree.complete();
        this.service.destructor();
    }

    /**
     * Фильтрация данных дерева
     * @param data - список узлов
     * @param search - строка поиска
     * @param dataType - тип списка узлов
     */
    public filterService(data: TreeNode[], search: string, dataType: 'all' | 'select') {

        this.filterState[dataType].index = -1;

        if (!data) {
            return;
        }
        // организация фильтрованного массива
        let dataTree = [];
        // получение значения строки поиска
        if (!search) {
            dataTree = data;
            this.highlightText[dataType] = '';
            this.filterState[dataType].disableUp = true;
            this.filterState[dataType].disableDown = true;
            this.activeNode = null;
        } else {
            const regExp = search.replace(/[еёЕЁ]/g, '[еёЕЁ]');
            const matchValue = new RegExp(regExp, 'i');

            this.filterState[dataType].disableUp = true;
            this.filterState[dataType].disableDown = false;

            data.forEach(parent => {

                if (dataType === 'all') {
                    this.highlightText.all = search;
                    const element: TreeNode = {name: parent.name, level: 1};
                    if (parent.children) {
                        parent.children.forEach(child => {
                            if (child.name.match(matchValue)) {
                                if (typeof element.children == 'undefined') {
                                    element.children = [];
                                }
                                element.children.push(child);
                            }
                        });
                    }
                    dataTree.push(element);
                } else {
                    this.highlightText.select = search;

                    if (parent.name.match(matchValue)) {
                        dataTree.push(parent);
                    }
                }
            });
        }
        if (dataType === 'select') {
            this.dataSourceSelect.data = dataTree;
            this.treeControlSelect.dataNodes = this.dataSourceSelect.data;
            this.treeControlSelect.expandAll();
        } else {
            this.dataSourceAll.data = dataTree;
            this.treeControlAll.dataNodes = this.dataSourceAll.data;
            this.treeControlAll.expandAll();
        }
    }

    /**  Сохранение данных */
    save() {
        this.data.settings.select = this.dataSelected;
        this.data.settings.all = this.dataAll;
        this.dialogRef.close({
            save: true,
            settings: this.data.settings
        });
    }

    /** Закрытие диалога */
    close() {
        this.dialogRef.close({save: false});
    }

    /**
     * Удаление ветки из дерева
     * @param {TreeNode[]} treeRemove дерево из которого переносят
     * @param {TreeNode[]} treeAdd дерево в которое переносят
     * @param {TreeNode} node ветка дерева
     * @param index - индекс, вставки, что бы переместить в конкретное место
     */
    moveNodeBetweenTree(treeRemove: TreeNode[], treeAdd: TreeNode[], node: TreeNode, index: number = null) {
        node.is_sticky = false;
        node.is_main = false;
        let parent_node, children, tree = [];
        treeRemove.forEach(parent => {
            children = [];
            if (treeRemove == this.dataAll) {
                if (!parent.children) {
                    parent.children = [];
                }
                parent.children.forEach(child => {
                    if (node == child) {
                        parent_node = parent;
                        node.parent = parent.name;
                    } else {
                        children.push(child);
                    }
                });
                parent.children = children;
            } else {
                if (parent == node) {
                    parent_node = parent;
                    node.level = 2;
                } else {
                    tree.push(parent);
                }
            }
        });
        if (tree.length > 0) {
            this.dataSelected = tree;
        }
        if (treeAdd == this.dataAll) {
            treeAdd.forEach(parent => {
                if (parent_node.columns.length == 0 && parent.name == parent_node.parent) {
                    if (!parent.children) {
                        parent.children = [];
                    }
                    parent.children.push(node);
                }
                if (parent_node.columns.length > 1) {
                    parent_node.columns.forEach(column => {
                        if (parent.name == column.parent) {
                            if (!parent.children) {
                                parent.children = [];
                            }
                            column.level = 2;
                            parent.children.push(column);
                        }
                    });
                }
            });
        } else {
            if (index !== null) {
                treeAdd.splice(index, 0, node);
            } else {
                treeAdd.push(node);
            }
        }
        this.refreshTree();
    }

    /**
     * Перемещение ветки по дереву
     * @param {TreeNode} node ветка дерева
     * @param {boolean} up направление движения ветки
     */
    moveNode(node: TreeNode, up: boolean) {
        let element_node, element_index = 0;
        this.dataSelected.forEach((parent, index) => {
            if (node == parent) {
                element_node = parent;
                element_index = index;
            }
        });
        if (element_node) {
            if (up && element_index > 0) {
                this.dataSelected[element_index] = this.dataSelected[element_index - 1];
                this.dataSelected[element_index - 1] = element_node;
            } else if (!up && element_index < (this.dataSelected.length - 1)) {
                this.dataSelected[element_index] = this.dataSelected[element_index + 1];
                this.dataSelected[element_index + 1] = element_node;
            }
        }
        this.refreshTree();
    }

    /**
     * Обновление деревьев
     */
    public refreshTree(): void {
        this.refreshNodeCheckbox();

        if (this.highlightText.select?.length > 0) {
            this.filterService(this.dataSelected, this.highlightText.select, 'select');
        } else {
            this.dataSourceSelect.data = this.dataSelected;
        }

        if (this.highlightText.all?.length > 0) {
            this.filterService(this.dataAll, this.highlightText.all, 'all');
        } else {
            this.dataSourceAll.data = this.dataAll;
        }
    }

    /**
     * Обработка перетаскивания
     * @param {CdkDragDrop<string[]>} event событие перетаскивания
     */
    drop(event: CdkDragDrop<string[]>) {
        switch (event.previousContainer.id) {
            case 'fields-tree':
                this.moveNodeBetweenTree(this.dataAll, this.dataSelected, event.item.data, event.currentIndex);
                break;
            case 'table-fields-tree':
                this.dataSelected.splice(event.previousIndex, 1);
                this.dataSelected.splice(event.currentIndex, 0, event.item.data);
                this.refreshTree();
                break;
        }
    }

    /**
     * Начало перетаскивания
     */
    dragStart() {
        this.dragging = true;
    }

    /**
     * Конец перетаскивания
     */
    dragEnd() {
        this.dragging = false;
    }

    /** Объединение колонок */
    unionNode() {
        const unionColumns = [];
        for (const checkbox in this.nodeCheckbox) {
            if (this.nodeCheckbox.hasOwnProperty(checkbox) && this.nodeCheckbox[checkbox]) {
                unionColumns.push(checkbox);
            }
        }
        this.openNameDialog('union', null, unionColumns);
    }

    /**
     * Разъединить колонки
     * @param {TreeNode} node ветка дерева
     */
    unUnionNode(node: TreeNode) {
        let settingsSelect = [];
        this.dataSelected.forEach(select => {
            if (select != node) {
                settingsSelect.push(select);
            }
        });
        node.columns.forEach(column => {
            settingsSelect.push(column);
        });
        this.dataSelected = settingsSelect;
        this.refreshTree();
    }

    /**
     * Открытие диалога задания имени
     * @param {string} type тип
     * @param {TreeNode|null} node ветка дерева
     * @param {string[]} unionColumns колонки для объединения
     * @param nodeRef
     */
    openNameDialog(type: string, node: TreeNode = null, unionColumns: string[] = null) {
        const dialogRef = this.dialog.open(SettingsTableNameDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '600px',
            data: {
                title: this.appService.language_dictionary.system_settings_table_dialog_title,
                name: node ? node.name : '',
                type: type
            }
        });
        dialogRef.keydownEvents().subscribe(result => {
            if (result.code === 'Escape') {
                dialogRef.close({save: false});
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result.save) {
                if (type === 'rename') {
                    node.name = result.name;
                    this.dataSelected = JSON.parse(JSON.stringify(this.dataSelected));
                    this.refreshTree();
                }
                if (type === 'union') {
                    const settingsSelect = [];
                    const unionColumnsNode = [];
                    this.dataSelected.forEach(select => {
                        if (unionColumns.indexOf(select.alias) === -1) {
                            settingsSelect.push(select);
                        } else {
                            unionColumnsNode.push(select);
                        }
                    });
                    this.dataSelected = settingsSelect;
                    this.dataSelected.push(
                        {
                            parent: '',
                            is_sticky: false,
                            is_main: false,
                            type: this.getTypeUnionColumns(unionColumnsNode),
                            width: 150,
                            name: result.name,
                            alias: unionColumns.join('_'),
                            columns: unionColumnsNode,
                            level: 1,
                            isUnion: true
                        }
                    );
                    this.refreshTree();
                }
                this.nodeCheckbox = [];
            }
        });
    }

    /**
     * Установка флага основной колонки
     * @param node - данные колонки
     */
    public setMainColumn(node: TreeNode): void {
        this.dataSelected.forEach(select => {
            select.is_main = node.alias == select.alias;
        });
    }

    /**
     * Очистка настроек таблицы
     */
    clearTableSettings() {
        this.dialogConfirm.confirm(
            this.appService.language_dictionary.system_confirm_action,
            this.appService.language_dictionary.system_reset_table_settings_modal
        ).subscribe((res) => {
                if (res) {
                    this.spinner.show();
                    let dataSettings: {
                        module?: string
                    } = {};
                    if (this.data.dynamicFilter) {
                        dataSettings = Object.assign(dataSettings, this.data.dynamicFilter);
                    }
                    if (this.data.tableModule) {
                        dataSettings.module = this.data.tableModule;
                    }
                    this.data.tableService.clearTableSettings(dataSettings).toPromise().then((result: IAppRequest) => {
                        this.spinner.hide();
                        if (result && result.is_error === false) {
                            this.notifier.notify('success', this.appService.language_dictionary.system_reset_table_settings_success);
                            location.reload();
                        } else {
                            this.notifier.notify('error', result.message);
                        }
                    }).catch(() => {
                        this.spinner.hide();
                        this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
                    });
                }
            }
        );
    }

    /**
     * Прокрутка поиска по списку
     * @param count - ступень прокрутки
     * @param typeTree - тип списка
     */
    public switchRowInFilteredData(count: -1 | 1, typeTree: 'all' | 'select'): void {

        let data = [];
        this.filterState[typeTree].index += count;

        if (typeTree === 'all') {
            this.dataSourceAll.data.forEach((item) => {
                item?.children?.forEach((child) => {
                    data.push(child);
                });
            });
        } else {
            data = this.dataSourceSelect.data;
        }

        if (this.highlightText[typeTree]?.length > 0) {
            this.filterState[typeTree].disableDown = (this.filterState[typeTree].index + 1) >= data.length;
            this.filterState[typeTree].disableUp = this.filterState[typeTree].index < 1;
        }

        const current = data[this.filterState[typeTree].index];

        this.itemsRefData[typeTree].items.forEach((item) => {
            if (current.alias === item.nativeElement.id) {
                this.itemsRefData[typeTree].container.scroll({
                    top: this.scrollingToElement(item.nativeElement, this.itemsRefData[typeTree].container),
                    behavior: 'smooth'
                });
            }
        });

        this.activeNode = current?.alias;

    }

    /**
     * Прокрутка блока до активного элемента
     * @param item - параметры активного элемента
     * @param container - объект блока
     */
    public scrollingToElement(item: HTMLElement, container: HTMLElement): number {
        return item.offsetTop >= container.offsetHeight ?
            item.offsetTop - ((item.offsetHeight * 2) + 20) :
            item.offsetTop - (item.offsetHeight * 8);
    }

    /**
     * Определить тип, для колонки которую объединили
     * @param unionColumnsNode - объединяемые колонки
     */
    public getTypeUnionColumns(unionColumnsNode: any[]): string {
        if (unionColumnsNode.some(column => column.type === 'gallery')
            || unionColumnsNode.some(column => column.type === 'file')
            || unionColumnsNode.some(column => column.type === 'HtmlTextType')) {
            return 'HtmlTextType';
        }
        if (unionColumnsNode.some(column => column.type === 'link')) {
            return 'link';
        }
        return 'string';
    }
}
